import React from 'react';
import { Badge, Descriptions, Drawer, Space, Tag, Typography } from 'antd';

import {
  getDeviceSymbolPath,
  getDeviceTypeConfig,
} from '@totem/components/portfolio/buildings/network/deviceTypeConfig';
import {
  NetworkDiagramNetwork,
  NetworkDiagramNode,
} from '@totem/components/portfolio/buildings/network/types';

const { Text } = Typography;

type Props = {
  node: NetworkDiagramNode;
  networks: NetworkDiagramNetwork[];
  onClose: () => void;
};

const STATUS_BADGE: Record<string, 'success' | 'error' | 'default'> = {
  online: 'success',
  offline: 'error',
  unknown: 'default',
};

const NetworkDeviceDetail = ({ node, networks, onClose }: Props) => {
  const networkMap = new Map(networks.map((net) => [net.id, net]));
  const badgeStatus =
    STATUS_BADGE[node.onlineStatus?.toLowerCase()] ?? 'default';

  return (
    <Drawer
      title={node.label || node.id}
      placement="right"
      width={340}
      open
      onClose={onClose}
      mask={false}
    >
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Status">
          <Badge status={badgeStatus} text={node.onlineStatus || 'Unknown'} />
        </Descriptions.Item>
        <Descriptions.Item label="Device Type">
          {node.deviceType ? (
            <Space size={6} align="center">
              <svg width="16" height="16" viewBox="-8 -8 16 16">
                <path
                  d={getDeviceSymbolPath(
                    getDeviceTypeConfig(node.deviceType).symbolType,
                  )}
                  fill={getDeviceTypeConfig(node.deviceType).color}
                />
              </svg>
              <span style={{ textTransform: 'capitalize' }}>
                {node.deviceType}
              </span>
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Make / Model">
          {node.makeModel || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Vendor">
          {node.vendorName || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="IP Addresses">
          {node.ipAddresses?.length ? (
            <Space direction="vertical" size={2}>
              {node.ipAddresses.map((ip) => (
                <Text key={ip} code>
                  {ip}
                </Text>
              ))}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Networks">
          {node.networkIds?.length ? (
            <Space wrap size={[4, 4]}>
              {node.networkIds.map((nid) => {
                const net = networkMap.get(nid);
                return (
                  <Tag key={nid} color="blue">
                    {net?.name ?? nid}
                  </Tag>
                );
              })}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
};

export default NetworkDeviceDetail;
